import { Mail } from "components/icons/Mail";
import { Refresh } from "components/icons/Refresh";
import { LongArrowRight } from "components/icons/LongArrowRight";
import ActiveCheckSquareOffset from "static/images/ActiveCheckSquareOffset.svg";
import { useHistory } from "react-router-dom";
import { useCreateMessageDraftMutation } from "store/apis/chat";
import React from "react";

type ActionCardDef = {
  icon: React.ReactNode;
  title: string;
  description: string;
  action: "good_standing" | "resolve_notice" | "fix_revive";
};

const PREFILL_MESSAGES: Record<string, string> = {
  good_standing:
    "Hi, I'd like to request a Good Standing Certificate for our entity. Could you help us get started?",
  resolve_notice:
    "Hi, we've received a state notice and would like help resolving it. Could you guide us on the next steps?",
  fix_revive:
    "Hi, our entity has been suspended or dissolved and we'd like to restore it to good standing. Could you help us with the reinstatement or revival process?",
};

const ACTION_CARD_DEFS: ActionCardDef[] = [
  {
    icon: (
      <img
        src={ActiveCheckSquareOffset}
        alt="ActiveCheckSquareOffset"
        className="t-size-4"
      />
    ),
    title: "Good Standing certificate",
    description:
      "Obtain an official certificate confirming your entity is active and compliant. Commonly required for banking, fundraising, and immigration applications.",
    action: "good_standing",
  },
  {
    icon: <Mail />,
    title: "Resolve a state notice",
    description:
      "Share any correspondence you have received from a State authority. Inkle will review it and advise on the required next steps.",
    action: "resolve_notice",
  },
  {
    icon: <Refresh size="14" />,
    title: "Fix or revive an entity",
    description:
      "Reinstate an entity that has been suspended, or flagged as non-compliant, and restore it to active good standing.",
    action: "fix_revive",
  },
];

export const StateRegistrationActionCards = ({
  generalHelpChannelUrl,
}: {
  generalHelpChannelUrl: string | undefined;
}) => {
  const history = useHistory();
  const [createUpdateDraft] = useCreateMessageDraftMutation();

  const handleChatAction = async (action: ActionCardDef["action"]) => {
    const message = PREFILL_MESSAGES[action];
    if (!message || !generalHelpChannelUrl) return;
    await createUpdateDraft({ channelUrl: generalHelpChannelUrl, message });
    history.push(`/chat/${generalHelpChannelUrl}`);
  };

  return (
    <div className="t-grid t-grid-cols-3 t-gap-3 sm:t-grid-cols-1">
      {ACTION_CARD_DEFS.map((card) => (
        <div
          key={card.title}
          className="t-flex t-flex-col t-gap-2 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4 t-cursor-pointer hover:t-bg-surface-lighter-grey t-transition-colors"
          onClick={() => void handleChatAction(card.action)}
        >
          <div className="t-flex t-items-center t-gap-2">
            <div className="t-flex t-h-7 t-w-7 t-shrink-0 t-items-center t-justify-center t-rounded-full t-bg-purple-0 t-text-purple">
              {card.icon}
            </div>
            <div className="t-flex-1 t-text-body-sm t-font-semibold t-text-text-100">
              {card.title}
            </div>
            <span className="t-text-text-30 t-flex-shrink-0">
              <LongArrowRight />
            </span>
          </div>
          <div className="t-text-body-sm t-text-text-30">
            {card.description}
          </div>
        </div>
      ))}
    </div>
  );
};
